import React, {
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import AscIcon from '@lib/assets/icons/asc_icon.svg?react';
import DescIcon from '@lib/assets/icons/desc_icon.svg?react';
import Modal from '@lib/components/Modal/Modal';
import RadioButtonGroup, {
  RadioButtonGroupProps,
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import CheckBoxGroup, {
  CheckBoxGroupProps,
} from '@lib/components/CheckBoxGroup/CheckBoxGroup';
import Input from '@lib/components/Input/Input';
import Select from '@lib/components/Select/Select';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import { Option } from '@lib/interfaces/form';
import Search from '@lib/components/Search/Search';
import TabBar from '@lib/components/TabBar/TabBar';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { Ordering, TabsType } from './enums';
import getTabs from './utils/getTabs';
import getFilteredOptions from './utils/getFilteredOptions';
import getSortingNavItems from './utils/getSortingNavItems';
import getSortedOptions from './utils/getSortedOptions';
import styles from './Checklist.module.scss';

type Props = {
  disabled?: boolean;
  hasError?: boolean;
  limitToOneResp?: boolean;
  onChange?: (v: Option[]) => void;
  options: Option[];
  value: Option[];
};

const Checklist = forwardRef(
  (
    props: (CheckBoxGroupProps | RadioButtonGroupProps) & Props,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      name: fieldName,
      onChange,
      options = [{ labelKey: 'no-options', value: '' }],
      value: selectedOptions = [],
      limitToOneResp,
      disabled,
      hasError,
    } = props;
    const { t } = useTranslation();
    const selectRef = useRef<any>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [showGradient, setShowGradient] = useState(false);
    const [orderingDirection, setOrderingDirection] = useState<Ordering>(
      Ordering.Default,
    );
    const [tab, setTab] = useState(TabsType.All);
    const [searchValue, setSearchValue] = useState('');
    const [isModalOpened, setModalOpen] = useState(false);

    const preparedOptions = useMemo(
      () =>
        getSortedOptions(
          getFilteredOptions(options, searchValue, tab, selectedOptions),
          orderingDirection,
        ),
      [options, orderingDirection, searchValue, selectedOptions, tab],
    );

    const handleSelectChange = (v: unknown) => {
      if (onChange) onChange([v as Option]);
    };

    const handleClear = () => {
      if (onChange) onChange([]);
    };

    const handleSearchChange = (v: string) => {
      setSearchValue(v);
    };

    const handleTabChange = ({ value }: TabBarItemType) =>
      setTab(value as TabsType);

    const handleCheckBoxGroupChange = (v: Option[]) => {
      if (onChange) onChange(v);
    };

    const handleOpenModal = () => {
      setModalOpen(true);
    };

    const handleCloseModal = () => setModalOpen(false);

    const handleScrollCheck = () => {
      const el = listRef.current;
      if (!el) return;
      setShowGradient(
        el.scrollHeight > el.clientHeight &&
          el.scrollTop + el.clientHeight < el.scrollHeight - 2,
      );
    };

    useEffect(() => {
      setTimeout(() => {
        handleScrollCheck();
      }, 0);
    }, [options]);

    useEffect(() => {
      window.addEventListener('resize', handleScrollCheck);
      return () => window.removeEventListener('resize', handleScrollCheck);
    }, []);

    const selectComponents = { ValueContainer: MultiSelectValueContainer };
    const sortingIcon =
      orderingDirection === Ordering.Default ||
      orderingDirection === Ordering.AtoZ ? (
        <AscIcon key="asc" />
      ) : (
        <DescIcon key="desc" />
      );
    const tabs = useMemo(
      () =>
        getTabs({
          t,
          allCount: options.length,
          selectedCount: selectedOptions.length,
        }),
      [options.length, selectedOptions.length, t],
    );
    const navItems = getSortingNavItems({
      t,
      orderingDirection,
      onSortingChange: setOrderingDirection,
    });

    const emptyStateActions = [
      <Button
        key="back"
        buttonText={t('go-back')}
        buttonType={ButtonTypes.primaryFilled}
        leftIcon="arrow_back"
        onClick={() => setSearchValue('')}
      />,
    ];
    const emptyState = searchValue?.length ? (
      <ListEmptyState
        title={t('no-results-found')}
        text={t('no-results-found-text')}
        actions={emptyStateActions}
      />
    ) : (
      <ListEmptyState
        title={t('no-options-selected-yet')}
        text={t('no-options-selected-text')}
      />
    );

    if (disabled) {
      return (
        <div className={styles.list}>
          <div
            className={styles.listScroll}
            ref={listRef}
            onScroll={handleScrollCheck}
          >
            {limitToOneResp ? (
              <RadioButtonGroup
                disabled
                options={options}
                wrapView={RadioButtonWrapViews.list}
                value={selectedOptions?.[0]}
              />
            ) : (
              <CheckBoxGroup
                disabled
                options={options}
                value={selectedOptions}
              />
            )}
          </div>
          {showGradient && <span className={styles.gradient} />}
        </div>
      );
    }

    const inputValue = selectedOptions?.length
      ? `${t('selected')} (${selectedOptions.length})`
      : undefined;

    return (
      <>
        <div className={styles.selectWrap}>
          {limitToOneResp ? (
            <Select
              components={selectComponents}
              disabled={disabled}
              hasError={hasError}
              isSearchable={false}
              label={t('choose-from-the-list')}
              name={fieldName}
              onChange={handleSelectChange}
              options={options}
              ref={selectRef}
              value={selectedOptions}
            />
          ) : (
            <Input
              disabled={disabled}
              hasError={hasError}
              label={t('choose-from-the-list')}
              onClear={handleClear}
              onClick={handleOpenModal}
              readOnly
              rightIcon="arrow_drop_down"
              value={inputValue}
            />
          )}
        </div>
        <Modal
          caption={t('choose-from-the-list')}
          isOpen={isModalOpened}
          modalBodyClassName={styles.modalBodyClassName}
          modalContentClassName={styles.modalContentClassName}
          onClose={handleCloseModal}
          actions={
            <Button
              buttonText={t('save-and-close')}
              onClick={handleCloseModal}
            />
          }
        >
          <>
            <TabBar
              items={tabs}
              active={tab}
              onActiveChange={handleTabChange}
              className={styles.tabsWrap}
            />
            <div className={styles.searchRow}>
              <Search
                onChange={handleSearchChange}
                placeholder={t('options-search-placeholder')}
                useBottomMargin={false}
                useSearchParam={false}
                value={searchValue}
              />
              <IconButtonWithDropDown
                buttonProps={{
                  svgIcon: sortingIcon,
                  className: styles.sortingBtn,
                }}
                popoverProps={{
                  width: 168,
                  placement: 'bottomRight',
                }}
                navItems={navItems}
              />
            </div>
            {preparedOptions?.length === 0 ? (
              emptyState
            ) : (
              <div className={styles.listWrap}>
                <div
                  className={styles.listScroll}
                  ref={listRef}
                  onScroll={handleScrollCheck}
                >
                  <CheckBoxGroup
                    value={selectedOptions}
                    options={preparedOptions}
                    onChange={handleCheckBoxGroupChange}
                  />
                </div>
                {showGradient && <span className={styles.gradient} />}
              </div>
            )}
          </>
        </Modal>
      </>
    );
  },
);

export default memo(Checklist);
